import { FieldProps, FormikContextType, connect, getIn } from 'formik';
// eslint-disable-next-line
import React, { FC, ReactNode, useCallback } from 'react';
import { useStableCompareEffect, useValueMemo } from 'utils/hooks';

import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import MenuItem from '@material-ui/core/MenuItem';
import MuiTextField from '@material-ui/core/TextField';
import RefreshIcon from '@material-ui/icons/Refresh';
import { TextFieldProps } from 'formik-material-ui';
import { capitalize } from 'utils/helpers';
import { customFieldToTextField, mapToSelectStyleProps } from './helpers';

type BindPropFunction<T> = T | ((obj: any) => T);

export type SelectProps = {
  capitalizeLabel?: boolean;
  items?: any[];
  bindValue?: string;
  bindLabel?: BindPropFunction<string>;
  customvalue?: string;
  custommenuitem?: ReactNode;
  inline?: boolean;
  isLoading?: boolean;
  error?: unknown;
  hideMenuItemOnSameInitialValue?: boolean;
  onRefresh?: () => any;
  sorted?: boolean;
  sortBy?: (a: any, b: any) => number;
  addNoneOption?: boolean;
  addNoneOptionLabel?: string;
  showIcons?: boolean;
  setIcons?: (value: string) => ReactNode;
  iconKey?: string;
  rightIcon?: boolean;
  justifyContentLabel?: string;
} & FieldProps &
  Partial<TextFieldProps>;

type OuterProps = { formik: FormikContextType<any> };

/**
 * @example
 * <Select items={[{ value: 1, label: 'texto' }]} />
 *
 * @example
 * Custom bindValue / bindLabel
 * <Select bindValue="id" bindLabel="description" items={ [{ id: 1, description: 'texto' }]} />
 *
 * @example
 * Custom bindLabel
 * <Select bindValue="id" bindLabel={({id, description} => `${id}-${description}`)} items={ [{ id: 1, description: 'texto' }]} />
 *
 * @example
 * Custom sort
 * <Select sortBy={(a, b) => a -b} items={ [{ id: 1, description: 'texto' }]} />
 *
 * @example
 * no sorting
 * <Select sorted={false} items={ [{ id: 1, description: 'texto' }]} />
 *
 *  * @example
 * addNoneOption renders an empty option with addNoneOptionLabel by default
 * <Select addNoneOption items={ [{ id: 1, description: 'texto' }]} />
 */

const Select: FC<OuterProps & SelectProps> = ({
  capitalizeLabel,
  customvalue,
  custommenuitem,
  items: originalItems,
  inline,
  placeholder,
  bindValue = 'value',
  bindLabel = 'label',
  isLoading,
  error,
  onRefresh,
  hideMenuItemOnSameInitialValue,
  sortBy,
  sorted = true,
  addNoneOption = false,
  addNoneOptionLabel = 'Ninguno',
  showIcons = false,
  iconKey,
  setIcons,
  rightIcon = true,
  justifyContentLabel = 'space-between',
  ...props
}) => {
  const { form, field, disabled } = props;
  const { setFieldValue } = form;

  const noneOption = {
    label: addNoneOptionLabel,
    value: '',
    useDefaultBind: true,
  };
  const items = originalItems?.length
    ? [...originalItems, ...(addNoneOption ? [noneOption] : [])]
    : originalItems;

  // handle custom values & auto select option when there is a single item
  useStableCompareEffect(() => {
    if (!items?.length) return;

    // check customvalue property
    if (customvalue) {
      setFieldValue(field.name, customvalue);
      return;
    }

    // set empty value when value is not present in items
    // SE LLAMA DOS VECES CON UN SELECT DE UN ITEM - DTH useGetPlans
    if (
      field.value &&
      !items?.find((item) => item[bindValue] === field.value)
    ) {
      setFieldValue(field.name, '');
      return;
    }

    // Select a single option is there is only one record
    // SE LLAMA DOS VECES CON UN SELECT DE UN ITEM - DTH useGetPlans
    if (
      !field.value &&
      items.length === 1 &&
      items[0].hasOwnProperty(bindValue) &&
      !disabled
    ) {
      const fieldValue = items[0][bindValue];
      setFieldValue(field.name, fieldValue);
    }
  }, [
    bindValue,
    customvalue,
    disabled,
    field.name,
    field.value,
    items,
    setFieldValue,
  ]);

  const getProp = (
    componentProp: SelectProps['bindLabel'],
    obj: string
  ): string => {
    switch (typeof componentProp) {
      case 'function':
        return componentProp(obj);
      case 'string':
        return obj[componentProp];
      default:
        return '';
    }
  };

  const defaultSort = useCallback(
    (a, b) => {
      if (typeof bindLabel === 'string' && !a.hasOwnProperty(bindLabel)) {
        return 0;
      }

      const labelA: string = getProp(bindLabel, a);
      const labelB: string = getProp(bindLabel, b);

      const valueA = getProp(bindValue, a);
      const valueB = getProp(bindValue, b);

      if (valueA === valueB) {
        return 0;
      }

      if (valueA == null || valueA === '') {
        return 1;
      }

      if (valueB == null || valueB === '') {
        return -1;
      }

      return labelA.localeCompare(labelB);
    },
    [bindLabel, bindValue]
  );

  const renderMenuItems = useValueMemo(() => {
    if (Array.isArray(items) && items.length) {
      const customSort = sorted ? sortBy || defaultSort : undefined;

      return items.sort(customSort).map((item, index) => {
        const key = index;
        const value = item?.useDefaultBind
          ? item.value
          : item.hasOwnProperty(bindValue)
          ? item[bindValue]
          : '';

        const label = item?.useDefaultBind
          ? item.label
          : getProp(bindLabel, item);

        const icons =
          showIcons &&
          iconKey &&
          item?.[iconKey] &&
          setIcons?.(item?.[iconKey]);

        const hideMenuItem =
          hideMenuItemOnSameInitialValue &&
          value === getIn(form.initialValues, field.name)
            ? 'none'
            : '';

        return (
          <MenuItem
            key={key}
            value={value}
            style={{ display: hideMenuItem, padding: '11px 16px' }}
            data-testid="select-menu-item"
          >
            <div
              style={{
                display: 'flex',
                justifyContent: justifyContentLabel,
                alignItems: 'center',
                width: '100%',
              }}
            >
              {!rightIcon && showIcons && icons && (
                <div style={{ display: 'flex', gap: '10px' }}>{icons}</div>
              )}
              {capitalizeLabel ? capitalize(label) : label}
              {rightIcon && showIcons && icons && (
                <div style={{ display: 'flex', gap: '10px' }}>{icons}</div>
              )}
            </div>
          </MenuItem>
        );
      });
    }

    return (
      <MenuItem value="" data-testid="select-menu-item">
        Seleccionar
      </MenuItem>
    );
  }, [
    bindLabel,
    bindValue,
    capitalizeLabel,
    defaultSort,
    field.name,
    form.initialValues,
    hideMenuItemOnSameInitialValue,
    items,
    sortBy,
    sorted,
  ]);

  if (isLoading || !!error) {
    const hasError: boolean = !!error;
    const icon = hasError ? (
      onRefresh && (
        <IconButton aria-label="Reintentar" onClick={onRefresh}>
          <RefreshIcon />
        </IconButton>
      )
    ) : (
      <CircularProgress color="inherit" thickness={6} size={24} />
    );

    const value = hasError ? 'Ocurrió un error' : 'Cargando Opciones...';

    return (
      <MuiTextField
        type="text"
        margin="dense"
        {...customFieldToTextField(props as TextFieldProps)}
        {...mapToSelectStyleProps({ inline, ...props })}
        disabled
        value={value}
        InputProps={{
          endAdornment: icon && (
            <InputAdornment position="end">{icon}</InputAdornment>
          ),
        }}
      />
    );
  }

  return (
    <MuiTextField
      select
      type="text"
      margin="dense"
      {...customFieldToTextField(props as TextFieldProps)}
      {...mapToSelectStyleProps({ inline, ...props })}
    >
      {custommenuitem || renderMenuItems}
    </MuiTextField>
  );
};

export default connect<SelectProps, any>(Select);
